import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, TrendingUp, TrendingDown, ShoppingBag, DollarSign, RefreshCw, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtMonth = (key) => {
  const [y, m] = key.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

export default function AdminSalesReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/admin/sales-report?months=12`, {
        headers: { 'Authorization': `Bearer ${pwd}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar relatório');
      setReport(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
    </div>
  );
  if (error) return (
    <div className="flex-1 flex flex-col items-center justify-center py-20 gap-3" data-testid="sales-report-error">
      <AlertCircle className="w-8 h-8 text-red-400" />
      <p className="text-slate-400 text-sm">{error}</p>
      <button onClick={fetchReport} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg">Tentar novamente</button>
    </div>
  );
  if (!report) return null;

  const { months, current_month, growth_percent, total_revenue, total_paid_orders, revenue_by_method } = report;
  const maxRevenue = Math.max(...months.map(m => m.revenue), 1);
  const growthUp = growth_percent !== null && growth_percent >= 0;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6" data-testid="sales-report">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4" data-testid="kpi-mes-atual">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-2"><DollarSign className="w-4 h-4 text-emerald-400" /> Faturamento (mês atual)</div>
          <p className="text-2xl font-bold text-white">{fmtBRL(current_month.revenue)}</p>
          <p className="text-[11px] text-slate-500 mt-1">{current_month.paid_orders} pedido(s) pago(s)</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4" data-testid="kpi-crescimento">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
            {growthUp ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />} Crescimento vs mês anterior
          </div>
          <p className={`text-2xl font-bold ${growthUp ? 'text-emerald-400' : 'text-red-400'}`}>
            {growth_percent === null ? '—' : `${growth_percent > 0 ? '+' : ''}${growth_percent}%`}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">Anterior: {fmtBRL(report.previous_month.revenue)}</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4" data-testid="kpi-total-12m">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-2"><DollarSign className="w-4 h-4 text-cyan-400" /> Faturamento (12 meses)</div>
          <p className="text-2xl font-bold text-white">{fmtBRL(total_revenue)}</p>
          <p className="text-[11px] text-slate-500 mt-1">{total_paid_orders} pedidos pagos no período</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4" data-testid="kpi-pedidos-mes">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-2"><ShoppingBag className="w-4 h-4 text-blue-400" /> Pedidos (mês atual)</div>
          <p className="text-2xl font-bold text-white">{current_month.orders}</p>
          <p className="text-[11px] text-slate-500 mt-1">{current_month.pending_orders} pendente(s)</p>
        </div>
      </div>

      {/* Gráfico de barras — faturamento mensal */}
      <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" /> Faturamento Mensal</h3>
          <button onClick={fetchReport} className="text-slate-400 hover:text-white" title="Atualizar" data-testid="sales-report-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-end gap-1.5 md:gap-3 h-44" data-testid="sales-chart">
          {months.map(m => (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full group">
              <span className="text-[10px] text-cyan-300 mb-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">{fmtBRL(m.revenue)}</span>
              <div
                className="w-full rounded-t bg-gradient-to-t from-cyan-600 to-blue-500 hover:from-cyan-500 hover:to-blue-400 transition-colors"
                style={{ height: `${Math.max((m.revenue / maxRevenue) * 100, m.revenue > 0 ? 4 : 1)}%`, minHeight: '2px' }}
              />
              <span className="text-[9px] md:text-[10px] text-slate-500 mt-1.5 whitespace-nowrap">{fmtMonth(m.month)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabela mensal + Métodos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-slate-800/60 border border-slate-700/60 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="sales-table">
            <thead>
              <tr className="bg-slate-900/60 text-slate-400 text-xs uppercase">
                <th className="text-left px-4 py-3">Mês</th>
                <th className="text-right px-4 py-3">Pedidos</th>
                <th className="text-right px-4 py-3">Pagos</th>
                <th className="text-right px-4 py-3">Faturamento</th>
              </tr>
            </thead>
            <tbody>
              {[...months].reverse().map(m => (
                <tr key={m.month} className="border-t border-slate-700/40 text-slate-300 hover:bg-slate-700/20">
                  <td className="px-4 py-2.5 font-medium">{fmtMonth(m.month)}</td>
                  <td className="px-4 py-2.5 text-right">{m.orders}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-400">{m.paid_orders}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-white">{fmtBRL(m.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4" data-testid="sales-by-method">
          <h3 className="text-white font-semibold text-sm mb-4">Faturamento por Método</h3>
          {Object.keys(revenue_by_method || {}).length === 0 ? (
            <p className="text-slate-500 text-xs">Nenhum pagamento aprovado ainda.</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(revenue_by_method).map(([method, value]) => (
                <div key={method}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-300">{method}</span>
                    <span className="text-white font-semibold">{fmtBRL(value)}</span>
                  </div>
                  <div className="h-2 bg-slate-900 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                      style={{ width: `${(value / Math.max(total_revenue, 1)) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
